/* ──────────────────────────────────────────────────────────────
   src/pages/reports/reservas/CancelacionTasaPage.tsx
   Informe – Tasa global de cancelación
   ────────────────────────────────────────────────────────────── */

import { useState } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
/* ─── UI ────────────────────────────────────────────────────── */
import {
  Stack,
  TextField,
  Button,
  CircularProgress,
  Paper,
  Typography,
  Alert,
} from "@mui/material";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from "recharts";

/* ─── hooks ─────────────────────────────────────────────────── */
import { useCancelacionTasaReport } from "../../../hooks/useReportJob";
import { downloadReportFile } from "../../../components/utils/downloadXlsx";

const COLORS = {
  alumno: "#1976d2", // azul primario MUI
  interna: "#ff9800", // ámbar
};

/* ──────────────────────────────────────────────────────────────
   Helpers locales
   ────────────────────────────────────────────────────────────── */

// Forzar el tipo string desde i18next (evita DefaultTFuncReturn en TS)
const ts = (s: unknown) => String(s);

// División segura con 0 → 0
const safeDiv = (num: number, den: number) => (den > 0 ? num / den : 0);

// Formateos
const fmtInt = (n: number) =>
  new Intl.NumberFormat("es-ES", { maximumFractionDigits: 0 }).format(n);

const fmtPct1 = (v: number) => `${(v * 100).toFixed(1)} %`;

/* ──────────────────────────────────────────────────────────────
   Componente tarjeta KPI
   ────────────────────────────────────────────────────────────── */
function MetricCard(props: {
  label: string;
  value: number | string;
  caption?: string;
  subcaption?: string;
}) {
  const { label, value, caption, subcaption } = props;
  return (
    <Paper sx={{ flex: 1, p: 2, textAlign: "center", minWidth: 180 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="h4" sx={{ lineHeight: 1.1 }}>
        {typeof value === "number" ? fmtInt(value) : value}
      </Typography>
      {caption && (
        <Typography variant="caption" color="text.secondary" display="block">
          {caption}
        </Typography>
      )}
      {subcaption && (
        <Typography variant="caption" color="text.secondary" display="block">
          {subcaption}
        </Typography>
      )}
    </Paper>
  );
}

/* ──────────────────────────────────────────────────────────────
   Página
   ────────────────────────────────────────────────────────────── */
export default function CancelacionTasaPage() {
  const { t } = useTranslation<"translation">();

  /* rango de fechas ------------------------------------------ */
  const [desde, setDesde] = useState(dayjs().startOf("month").format("YYYY-MM-DD"));
  const [hasta, setHasta] = useState(dayjs().format("YYYY-MM-DD"));

  /* job + polling -------------------------------------------- */
  const { run, useStatus } = useCancelacionTasaReport();
  const [jobId, setJobId] = useState<string>();
  const { data: status } = useStatus(jobId);
  const res = status?.result; // payload del worker

  /* lanzar informe ------------------------------------------- */
  const lanzar = async () => {
    const { jobId } = await run({ desde, hasta });
    setJobId(jobId);
  };

  const handleDownload = async () => {
    if (res?.url) {
      try {
        await downloadReportFile(res.url);
      } catch (error) {
        console.error("Error al descargar:", error);
      }
    }
  };

  // Derivados defensivos
  const totOK = res?.totOK ?? 0;
  const cancelTot = res?.cancelTot ?? 0;
  const cancelAlu = res?.cancelAlu ?? 0;
  const cancelInt = res?.cancelInt ?? 0;
  const totalReservas = totOK + cancelTot;

  // % sobre total de reservas (OK + canceladas)
  const pctCancelSobreTotal = safeDiv(cancelTot, totalReservas);
  const pctAluSobreTotal = safeDiv(cancelAlu, totalReservas);
  const pctIntSobreTotal = safeDiv(cancelInt, totalReservas);

  // % del total cancelado (partición del donut)
  const pctAluSobreCancelado = safeDiv(cancelAlu, cancelTot);
  const pctIntSobreCancelado = safeDiv(cancelInt, cancelTot);

  const donutData = [
    {
      name: ts(t("cancelacionTasa.chart.alumno", "Alumno")),
      value: cancelAlu,
      color: COLORS.alumno,
    },
    {
      name: ts(t("cancelacionTasa.chart.interna", "Interna")),
      value: cancelInt,
      color: COLORS.interna,
    },
  ];

  // Estado vacío
  const noDatos = status?.done && totalReservas === 0;

  return (
    <Stack spacing={3}>
      <Typography variant="h5">{ts(t("cancelacionTasa.titulo", "Tasa de cancelación"))}</Typography>

      {/* filtros: rango fechas + botón ------------------------ */}
      <Stack direction="row" spacing={2} alignItems="center">
        <TextField
          type="date"
          label={ts(t("cancelacionTasa.filtros.desde", "Desde"))}
          value={desde}
          onChange={(e) => setDesde(e.target.value)}
        />
        <TextField
          type="date"
          label={ts(t("cancelacionTasa.filtros.hasta", "Hasta"))}
          value={hasta}
          onChange={(e) => setHasta(e.target.value)}
        />
        <Button variant="contained" onClick={lanzar}>
          {ts(t("cancelacionTasa.filtros.generar", "Generar"))}
        </Button>
      </Stack>

      {/* progreso --------------------------------------------- */}
      {status && !status.done && (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={24} />
          <Typography>{ts(t("cancelacionTasa.estado.generando", "Generando informe…"))}</Typography>
        </Stack>
      )}

      {/* resultado -------------------------------------------- */}
      {status?.done && res && (
        <Stack spacing={3}>
          {/* estado vacío */}
          {noDatos && (
            <Alert severity="info">
              {ts(
                t("cancelacionTasa.estado.sinDatos", "No hay reservas en el rango seleccionado.")
              )}
            </Alert>
          )}

          {/* enlace descarga XLSX */}
          {res.url && (
            <Typography>
              {ts(t("cancelacionTasa.estado.xlsx", "XLSX"))}:&nbsp;
              <Button
                variant="text"
                size="small"
                onClick={handleDownload}
                sx={{ textTransform: "none", textDecoration: "underline", p: 0, minWidth: "auto" }}
              >
                {ts(t("cancelacionTasa.estado.descargar", "Descargar"))}
              </Button>
            </Typography>
          )}

          {/* tarjetas KPI */}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <MetricCard
              label={ts(t("cancelacionTasa.kpi.validas", "Reservas no canceladas"))}
              value={totOK}
              caption={ts(t("cancelacionTasa.kpi.validasCaption", "Reservas con estado OK"))}
            />

            <MetricCard
              label={ts(t("cancelacionTasa.kpi.total", "Cancelaciones (total)"))}
              value={cancelTot}
              caption={`${fmtPct1(pctCancelSobreTotal)} (${ts(
                t("cancelacionTasa.kpi.totalCaption", "% sobre total de reservas")
              )})`}
            />

            <MetricCard
              label={ts(t("cancelacionTasa.kpi.alumno", "Cancelaciones — alumno"))}
              value={cancelAlu}
              caption={`${fmtPct1(pctAluSobreTotal)} (${ts(
                t("cancelacionTasa.kpi.sobreTotal", "% sobre total de reservas")
              )})`}
              subcaption={`${fmtPct1(pctAluSobreCancelado)} (${ts(
                t("cancelacionTasa.kpi.sobreCancelado", "% del total cancelado")
              )})`}
            />

            <MetricCard
              label={ts(t("cancelacionTasa.kpi.interna", "Cancelaciones — internas"))}
              value={cancelInt}
              caption={`${fmtPct1(pctIntSobreTotal)} (${ts(
                t("cancelacionTasa.kpi.sobreTotal", "% sobre total de reservas")
              )})`}
              subcaption={`${fmtPct1(pctIntSobreCancelado)} (${ts(
                t("cancelacionTasa.kpi.sobreCancelado", "% del total cancelado")
              )})`}
            />
          </Stack>

          {/* gráfico donut ---------------------------------- */}
          <Paper sx={{ p: 2, maxWidth: 480, mx: "auto" }}>
            <Typography variant="subtitle2" align="center" sx={{ mb: 1 }}>
              {ts(t("cancelacionTasa.chart.titulo", "Distribución de cancelaciones"))}
            </Typography>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={donutData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={60}
                  outerRadius={100}
                  isAnimationActive={false}
                  label={({ name, value }: any) => `${name}: ${fmtInt(value ?? 0)}`}
                >
                  {donutData.map((d, i) => (
                    <Cell key={i} fill={d.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: any, name: any) => {
                    const v = Number(value) || 0;
                    const pctSobreCancelado = safeDiv(v, cancelTot);
                    return [
                      `${fmtInt(v)} (${fmtPct1(pctSobreCancelado)} ${ts(
                        t("cancelacionTasa.kpi.sobreCancelado", "% del total cancelado")
                      )})`,
                      name,
                    ];
                  }}
                />
                <Legend verticalAlign="bottom" />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Stack>
      )}
    </Stack>
  );
}
